import "./style.scss";
import MultiplierSvg from "@/src/svg/CustomUtils/MultiplierSvg";
import ApproveSvg from "@/src/svg/CustomUtils/ApproveSvg";
import CloseSvg from "@/src/svg/CustomUtils/CloseSvg";
import ArrowSvg from "@/src/svg/autoCompile/ArrowSvg";
import CalendarSvg from "@/src/svg/CustomUtils/CalendarSvg";
import { useEffect, useMemo, useRef, useState } from "react";
import { officeAccountingPageRequests } from "@/src/api/officeAccountingPageRequests";
import {
  IOfficeAccountingIncomeCreatePayload,
  IOfficeAccountingIncomeRecord,
  OfficeAccountingPaymentCreateType,
} from "@/src/types/officeAccounting";
import { formatOfficeAccountingIncomeRecordsTotal, sumOfficeAccountingIncomeRecords } from "@/src/utils/officeAccountingMappers";
import {
  formatOfficeAccountingUsdAmount,
  formatOfficeAccountingEurAmount,
  parseOfficeAccountingAmount,
} from "@/src/utils/officeAccountingTotalAmountUsd";
import { useFormatDateDisplay } from "@/src/hooks/useFormatDateDisplay";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import CustomSingleSelect from "@/src/components/CustomUtils/CustomSingleSelect/CustomSingleSelect";
import {
  resolveStatusInvitationFeeFromAmounts,
  resolveBalanceStatusUsdFromAmounts,
  resolveStatusMissedClassesFromAmounts,
  resolveStatusNostrificationFromAmounts,
} from "@/src/constants/officeAccountingTableConfig";
import { PAYMENT_FACT_EUR_INVOICE } from "@/src/utils/officeAccountingTotalAmountUsd";
import { buildSimpleHistoryText, createApplicantHistory } from "@/src/utils/applicants/createApplicantHistory";

export type CustomIncomeManagementMode = "invitation" | "payment" | "paymentEur";

const PAYMENT_CREATE_TYPE_BY_MODE: Record<CustomIncomeManagementMode, OfficeAccountingPaymentCreateType> = {
  invitation: "Invitation",
  payment: "PaymentUsdt",
  paymentEur: "PaymentEuro",
};

const INCOME_MODE_LABEL: Record<CustomIncomeManagementMode, string> = {
  invitation: "Invitation fee payment",
  payment: "Payment fact USD",
  paymentEur: "Payment fact EUR",
};

type Props = {
  title: string;
  mode: CustomIncomeManagementMode;
  accountingId?: number;
  applicantId?: number;
  invitationFee?: string;
  totalAmountUsd?: string;
  totalMissedClassesEur?: string;
  totalNostrificationsEur?: string;
  records?: IOfficeAccountingIncomeRecord[];
  invoiceOptions?: string[];
  onRefresh?: () => void | Promise<void>;
  onClose: () => void;
};

const ACCOUNT_OPTIONS = ["IEU PKO", "IEU Consaltens", "Main account"];
const PAYMENT_EUR_CHOOSE_OPTIONS = [
  PAYMENT_FACT_EUR_INVOICE.MISSED_CLASSES,
  PAYMENT_FACT_EUR_INVOICE.NOTIFICATION,
];
const CURRENCY_OPTIONS = ["USD", "EUR", "UAH"] as const;
type IncomeCurrency = (typeof CURRENCY_OPTIONS)[number];
type ResultCurrency = "USD" | "EUR";

/** Fallback: how many base-currency units for 1 unit of selected currency. */
const FALLBACK_USD_PER_UNIT: Record<IncomeCurrency, number> = {
  USD: 1,
  EUR: 1.08,
  UAH: 0.024,
};

const FALLBACK_EUR_PER_UNIT: Record<IncomeCurrency, number> = {
  USD: 0.92,
  EUR: 1,
  UAH: 0.022,
};

const getTodayDate = () => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatCoefficientInput = (rate: number): string => {
  if (!Number.isFinite(rate) || rate <= 0) return "x1.00";
  const digits = rate >= 1 ? 2 : 4;
  const trimmed = rate.toFixed(digits).replace(/\.?0+$/, "");
  return `x${trimmed || "1"}`;
};

type IncomeFormState = Omit<IOfficeAccountingIncomeCreatePayload, "applicant_accounting_id" | "type">;

const emptyForm = (
  ratesPerUnit: Record<IncomeCurrency, number>,
  defaultCurrency: IncomeCurrency = "USD",
): IncomeFormState => ({
  date: getTodayDate(),
  invoice_number: "",
  count: "",
  currency: defaultCurrency,
  coefficient: formatCoefficientInput(ratesPerUnit[defaultCurrency]),
  account: "",
  result: "",
  note: "",
});

const parseNumericInput = (raw: string): number | null => {
  const cleaned = String(raw ?? "")
    .replace(/x/gi, "")
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .trim();
  if (!cleaned) return null;
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatResultInput = (amount: number): string => {
  const [intPart, decPart] = amount.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart}`;
};

const formatCountDisplay = (count: string, currency: string): string => {
  const amount = parseOfficeAccountingAmount(count);
  const label = currency.trim() || "USD";
  if (!String(count ?? "").trim()) return `0.00 ${label}`;
  return `${formatResultInput(amount)} ${label}`;
};

const formatCoefficientDisplay = (coefficient: string): string => {
  const trimmed = String(coefficient ?? "").trim();
  if (!trimmed) return "x0.00";
  return trimmed.startsWith("x") || trimmed.startsWith("X") ? trimmed : `x${trimmed}`;
};

const isIncomeCurrency = (value: string): value is IncomeCurrency =>
  (CURRENCY_OPTIONS as readonly string[]).includes(value);

/** Convert open.er-api base rates → base currency per 1 unit of currency. */
const mapBaseRatesToPerUnit = (
  rates: Record<string, number>,
  base: ResultCurrency,
  fallback: Record<IncomeCurrency, number>,
): Record<IncomeCurrency, number> => {
  const next: Record<IncomeCurrency, number> = { ...fallback };
  CURRENCY_OPTIONS.forEach((currency) => {
    if (currency === base) {
      next[currency] = 1;
      return;
    }
    const unitsPerBase = rates[currency];
    if (typeof unitsPerBase === "number" && Number.isFinite(unitsPerBase) && unitsPerBase > 0) {
      next[currency] = 1 / unitsPerBase;
    }
  });
  return next;
};

const fetchRatesPerUnit = async (base: ResultCurrency): Promise<Record<IncomeCurrency, number>> => {
  const fallback = base === "EUR" ? FALLBACK_EUR_PER_UNIT : FALLBACK_USD_PER_UNIT;
  try {
    const response = await fetch(`https://open.er-api.com/v6/latest/${base}`);
    if (!response.ok) return fallback;
    const data = (await response.json()) as { result?: string; rates?: Record<string, number> };
    if (data.result !== "success" || !data.rates) return fallback;
    return mapBaseRatesToPerUnit(data.rates, base, fallback);
  } catch {
    return fallback;
  }
};

const formatResultAmount = (amount: number, currency: ResultCurrency): string =>
  currency === "EUR" ? formatOfficeAccountingEurAmount(amount) : formatOfficeAccountingUsdAmount(amount);

const CustomIncomeManagement = ({
  title,
  mode,
  accountingId,
  applicantId,
  invitationFee = "",
  totalAmountUsd = "",
  totalMissedClassesEur = "",
  totalNostrificationsEur = "",
  records = [],
  invoiceOptions = [],
  onRefresh,
  onClose,
}: Props) => {
  const { notify } = usePushNotifications();
  const formatDateDisplay = useFormatDateDisplay();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const resultCurrency: ResultCurrency = mode === "paymentEur" ? "EUR" : "USD";
  const defaultCurrency: IncomeCurrency = resultCurrency;
  const fallbackRates = resultCurrency === "EUR" ? FALLBACK_EUR_PER_UNIT : FALLBACK_USD_PER_UNIT;

  const [ratesPerUnit, setRatesPerUnit] = useState<Record<IncomeCurrency, number>>(fallbackRates);
  const [form, setForm] = useState<IncomeFormState>(() => emptyForm(fallbackRates, defaultCurrency));
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [coefficientTouched, setCoefficientTouched] = useState(false);
  const [openSelector, setOpenSelector] = useState<"invoice" | "account" | null>(null);
  const [accountOptions, setAccountOptions] = useState<string[]>(ACCOUNT_OPTIONS);

  const isPaymentEur = mode === "paymentEur";

  const resolvedInvoiceOptions = useMemo(() => {
    if (isPaymentEur) return PAYMENT_EUR_CHOOSE_OPTIONS;
    const fromProps = invoiceOptions.map((item) => item.trim()).filter(Boolean);
    return Array.from(new Set(fromProps));
  }, [invoiceOptions, isPaymentEur]);

  const historyTotal = useMemo(
    () =>
      formatOfficeAccountingIncomeRecordsTotal(records, resultCurrency) || formatResultAmount(0, resultCurrency),
    [records, resultCurrency],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchRatesPerUnit(resultCurrency).then((rates) => {
      if (cancelled) return;
      setRatesPerUnit(rates);
      setForm((prev) => {
        if (coefficientTouched) return prev;
        const currency = isIncomeCurrency(prev.currency) ? prev.currency : defaultCurrency;
        const nextCoefficient = formatCoefficientInput(rates[currency]);
        if (prev.coefficient === nextCoefficient) return prev;
        return { ...prev, coefficient: nextCoefficient };
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load rates once on mount / mode
  }, [resultCurrency]);

  // Currency select drives the base rate into multiplier (unless user edited it).
  useEffect(() => {
    if (coefficientTouched) return;
    const currency = isIncomeCurrency(form.currency) ? form.currency : defaultCurrency;
    const nextCoefficient = formatCoefficientInput(ratesPerUnit[currency]);
    setForm((prev) => (prev.coefficient === nextCoefficient ? prev : { ...prev, coefficient: nextCoefficient }));
  }, [form.currency, ratesPerUnit, coefficientTouched, defaultCurrency]);

  // Result in base currency = value × rate (coefficient).
  useEffect(() => {
    const count = parseNumericInput(form.count);
    const coefficient = parseNumericInput(form.coefficient);
    if (count == null || coefficient == null) {
      setForm((prev) => (prev.result === "" ? prev : { ...prev, result: "" }));
      return;
    }
    const nextResult = formatResultInput(count * coefficient);
    setForm((prev) => (prev.result === nextResult ? prev : { ...prev, result: nextResult }));
  }, [form.count, form.coefficient]);

  const handleOpenDatePicker = () => {
    if (!dateInputRef.current) return;
    if (typeof dateInputRef.current.showPicker === "function") {
      dateInputRef.current.showPicker();
    } else {
      dateInputRef.current.focus();
      dateInputRef.current.click();
    }
  };

  const handleCurrencyChange = (currency: string) => {
    setCoefficientTouched(false);
    const resolved = isIncomeCurrency(currency) ? currency : defaultCurrency;
    setForm((prev) => ({
      ...prev,
      currency: resolved,
      coefficient: formatCoefficientInput(ratesPerUnit[resolved]),
    }));
  };

  const handleClearForm = () => {
    if (!window.confirm("Clear the entered income values?")) return;
    setCoefficientTouched(false);
    setForm(emptyForm(ratesPerUnit, defaultCurrency));
  };

  const handleApprove = async () => {
    if (!accountingId || accountingId <= 0) {
      notify({ title: "Save failed", message: "Accounting record id is missing.", type: "error" });
      return;
    }
    if (isSubmitting) return;

    const payload: Omit<IOfficeAccountingIncomeCreatePayload, "applicant_accounting_id"> = {
      date: form.date.trim(),
      invoice_number: form.invoice_number.trim(),
      count: form.count.trim().replace(/\s/g, ""),
      currency: form.currency.trim() || defaultCurrency,
      coefficient: form.coefficient.trim().replace(/^x/i, "").replace(/\s/g, ""),
      account: form.account.trim(),
      result: form.result.trim().replace(/\s/g, ""),
      note: form.note.trim(),
      type: PAYMENT_CREATE_TYPE_BY_MODE[mode],
    };

    console.groupCollapsed(`[Income] create ${mode} (applicant_accounting_id=${accountingId})`);
    console.log("payload:", { ...payload, applicant_accounting_id: accountingId });
    console.groupEnd();

    setIsSubmitting(true);
    try {
      const result = await officeAccountingPageRequests.createPayment(accountingId, payload);

      if (!result.ok) {
        notify({
          title: "Save failed",
          message: result.message || "Could not create income record.",
          type: "error",
        });
        return;
      }

      const nextPaidTotal =
        sumOfficeAccountingIncomeRecords(records) + parseOfficeAccountingAmount(payload.result);

      if (mode === "invitation") {
        const nextStatus = resolveStatusInvitationFeeFromAmounts(nextPaidTotal, invitationFee);
        await officeAccountingPageRequests.updateApplicantAccounting(accountingId, {
          status_invitation_fee: nextStatus,
        });
      } else if (mode === "payment") {
        const nextStatus = resolveBalanceStatusUsdFromAmounts(totalAmountUsd, nextPaidTotal);
        await officeAccountingPageRequests.updateApplicantAccounting(accountingId, {
          balance_status_usd: nextStatus,
        });
      } else {
        const chooseType = payload.invoice_number;
        const nextPaidForType =
          sumOfficeAccountingIncomeRecords(
            records.filter((record) => (record.invoice_number ?? "").trim() === chooseType),
          ) + parseOfficeAccountingAmount(payload.result);

        if (chooseType === PAYMENT_FACT_EUR_INVOICE.NOTIFICATION) {
          const nextStatus = resolveStatusNostrificationFromAmounts(
            totalNostrificationsEur,
            nextPaidForType,
          );
          await officeAccountingPageRequests.updateApplicantAccounting(accountingId, {
            status_nostrification: nextStatus,
          });
        } else {
          const nextStatus = resolveStatusMissedClassesFromAmounts(
            totalMissedClassesEur,
            nextPaidForType,
          );
          await officeAccountingPageRequests.updateApplicantAccounting(accountingId, {
            status_missed_classes: nextStatus,
          });
        }
      }

      await createApplicantHistory(
        applicantId,
        buildSimpleHistoryText(
          "Office Accounting",
          `${INCOME_MODE_LABEL[mode]} recorded (result: ${payload.result || "0"} ${resultCurrency}${
            payload.invoice_number ? `, invoice: ${payload.invoice_number}` : ""
          })`,
        ),
      );

      notify({
        title: "Saved",
        message:
          mode === "invitation"
            ? "Invitation fee payment added."
            : mode === "paymentEur"
              ? "Payment fact EUR added."
              : "Payment fact added.",
        type: "success",
      });
      setCoefficientTouched(false);
      setForm(emptyForm(ratesPerUnit, defaultCurrency));
      await onRefresh?.();
    } catch (error) {
      console.error("CustomIncomeManagement approve error:", error);
      notify({ title: "Save failed", message: "Could not create income record.", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="custom-income">
      <div className="custom-income-title">
        <div className="custom-income-title-text">
          <p>{title}</p>
        </div>
        <div className="custom-income-title-close" onClick={onClose}>
          <CloseSvg />
        </div>
      </div>
      <div className="custom-income-calc">
        <div className="custom-income-calc-date" onClick={handleOpenDatePicker}>
          <input
            ref={dateInputRef}
            type="date"
            value={form.date}
            onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
          />
          <CalendarSvg />
        </div>
        <div className="custom-income-calc-value">
          <div className="custom-income-calc-value-input">
            <input
              type="text"
              placeholder="0.00"
              value={form.count}
              onChange={(e) => setForm((prev) => ({ ...prev, count: e.target.value }))}
            />
          </div>
          <div className="custom-income-calc-value-select">
            <select value={form.currency} onChange={(e) => handleCurrencyChange(e.target.value)}>
              {CURRENCY_OPTIONS.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
            <ArrowSvg />
          </div>
        </div>
        <div className="custom-income-calc-multiplier">
          <input
            type="text"
            placeholder="x0.00"
            value={form.coefficient}
            onChange={(e) => {
              setCoefficientTouched(true);
              const raw = e.target.value;
              const withPrefix = raw === "" || raw.startsWith("x") || raw.startsWith("X") ? raw : `x${raw}`;
              setForm((prev) => ({ ...prev, coefficient: withPrefix }));
            }}
          />
          <div className="custom-income-calc-multiplier-line"></div>
          <MultiplierSvg />
        </div>
        <div className="custom-income-calc-result">
          <div className="custom-income-calc-result-title">
            <p>Result:</p>
          </div>
          <div className="custom-income-calc-result-input">
            <input type="text" placeholder="0.00" value={form.result} readOnly />
            <p>{resultCurrency}</p>
          </div>
        </div>
      </div>
      <div className="custom-income-selectors">
        <div className="custom-income-selectors-item">
          <div className="custom-income-selectors-item-label">
            <p>{isPaymentEur ? "Choose" : "Invoice No"}</p>
          </div>
          <div className="custom-income-selectors-item-select">
            <input
              readOnly
              placeholder={isPaymentEur ? "Please Choose" : "Please Choose Invoice"}
              value={form.invoice_number}
              onClick={() => setOpenSelector((prev) => (prev === "invoice" ? null : "invoice"))}
            />
            <ArrowSvg />
            {openSelector === "invoice" && (
              <CustomSingleSelect
                title={isPaymentEur ? "Choose" : "Invoice No"}
                data={resolvedInvoiceOptions}
                allowAdd={false}
                columnValue={form.invoice_number}
                onChange={(value) => setForm((prev) => ({ ...prev, invoice_number: value }))}
                onClose={() => setOpenSelector(null)}
              />
            )}
          </div>
        </div>
        <div className="custom-income-selectors-item">
          <div className="custom-income-selectors-item-label">
            <p>To Account:</p>
          </div>
          <div className="custom-income-selectors-item-select">
            <input
              readOnly
              placeholder="Please Choose"
              value={form.account}
              onClick={() => setOpenSelector((prev) => (prev === "account" ? null : "account"))}
            />
            <ArrowSvg />
            {openSelector === "account" && (
              <CustomSingleSelect
                title="To Account"
                data={accountOptions}
                allowAdd={true}
                columnValue={form.account}
                onChange={(value) => setForm((prev) => ({ ...prev, account: value }))}
                onClose={() => setOpenSelector(null)}
                onAddItem={(value) =>
                  setAccountOptions((prev) => (prev.includes(value) ? prev : [...prev, value]))
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className="custom-income-note">
        <textarea
          placeholder="Note..."
          value={form.note}
          onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
        />
      </div>
      <div className="custom-income-history">
        <div className="custom-income-history-header">
          <div className="custom-income-history-header-label">
            <p>History</p>
          </div>
          <div className="custom-income-history-header-icons">
            <div
              className={`custom-income-history-header-icons-approve ${isSubmitting ? "disabled" : ""}`}
              onClick={() => {
                if (!isSubmitting) void handleApprove();
              }}
            >
              <ApproveSvg />
            </div>
            <div className="custom-income-history-header-icons-close" onClick={handleClearForm}>
              <CloseSvg />
            </div>
            <div
              className={`custom-income-history-header-icons-arrow ${isHistoryOpen ? "open" : ""}`}
              onClick={() => setIsHistoryOpen((prev) => !prev)}
            >
              <ArrowSvg />
            </div>
          </div>
        </div>
        {isHistoryOpen && (
          <div className="custom-income-history-list">
            {records.length === 0 ? (
              <div className="custom-income-history-list-empty">
                <p>No records yet.</p>
              </div>
            ) : (
              records.map((record, index) => {
                const key = record.id ?? `${record.date ?? ""}-${record.invoice_number ?? ""}-${index}`;
                const dateLabel = record.date ? formatDateDisplay(record.date, { dateOnly: true }) : "—";
                return (
                  <div className="custom-income-history-list-item" key={key}>
                    <div className="custom-income-history-list-item-main">
                      <div className="custom-income-history-list-item-main-date">
                        <p>{dateLabel}</p>
                      </div>
                      <div className="custom-income-history-list-item-main-invoice">
                        <p>{record.invoice_number || "—"}</p>
                      </div>
                      <div className="custom-income-history-list-item-main-value">
                        <p>{formatCountDisplay(record.count ?? "", record.currency ?? "USD")}</p>
                      </div>
                    </div>
                    <div className="custom-income-history-list-item-multiplier">
                      <p>{formatCoefficientDisplay(record.coefficient ?? "")}</p>
                    </div>
                    <div className="custom-income-history-list-item-result">
                      <div className="custom-income-history-list-item-result-label">
                        <p>{record.account || "—"}</p>
                      </div>
                      <div className="custom-income-history-list-item-result-value">
                        <p>
                          {record.result
                            ? formatResultAmount(parseOfficeAccountingAmount(record.result), resultCurrency)
                            : formatResultAmount(0, resultCurrency)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
        <div className="custom-income-history-total">
          <p>Total</p>
          <p>{historyTotal}</p>
        </div>
      </div>
    </div>
  );
};

export default CustomIncomeManagement;
